<script>
import { HorizontalBar, mixins } from 'vue-chartjs';
import ChartjsPluginAnnotation from 'chartjs-plugin-annotation';
import ChartjsPluginDataLabels from 'chartjs-plugin-datalabels';
import ChartjsPluginStyle from 'chartjs-plugin-style';

Chart.plugins.unregister(ChartjsPluginDataLabels);

Chart.defaults.global.defaultFontColor = '#242424';
Chart.defaults.global.defaultFontFamily = 'Montserrat, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif';

Chart.elements.Rectangle.prototype.draw = function () {
  let left; let right; let top; let bottom; let signX; let signY; let borderSkipped; var
    radius;
  let barSize; let nextCornerId; let nextCorner; let width; let height; let x; let
    y;
  const { ctx } = this._chart;
  const vm = this._view;
  let { borderWidth } = vm;
  const cornerRadius = 8;

  if (! vm.horizontal) {
    left = vm.x - vm.width / 2;
    right = vm.x + vm.width / 2;
    top = vm.y;
    bottom = vm.base;
    signX = 1;
    signY = bottom > top ? 1 : -1;
    borderSkipped = vm.borderSkipped || 'bottom';
  } else {
    left = vm.base;
    right = vm.x;
    top = vm.y - vm.height / 2;
    bottom = vm.y + vm.height / 2;
    signX = right > left ? 1 : -1;
    signY = 1;
    borderSkipped = vm.borderSkipped || 'left';
  }

  if (borderWidth) {
    barSize = Math.min(Math.abs(left - right), Math.abs(top - bottom));
    borderWidth = borderWidth > barSize ? barSize : borderWidth;

    const halfStroke = borderWidth / 2;
    const borderLeft = left + (borderSkipped !== 'left' ? halfStroke * signX : 0);
    const borderRight = right + (borderSkipped !== 'right' ? -halfStroke * signX : 0);
    const borderTop = top + (borderSkipped !== 'top' ? halfStroke * signY : 0);
    const borderBottom = bottom + (borderSkipped !== 'bottom' ? -halfStroke * signY : 0);

    if (borderLeft !== borderRight) {
      top = borderTop;
      bottom = borderBottom;
    }

    if (borderTop !== borderBottom) {
      left = borderLeft;
      right = borderRight;
    }
  }

  ctx.beginPath();
  ctx.fillStyle = vm.backgroundColor;
  ctx.strokeStyle = vm.borderColor;
  ctx.lineWidth = borderWidth;

  const corners = [
    [left, bottom],
    [left, top],
    [right, top],
    [right, bottom],
  ];

  const borders = ['bottom', 'left', 'top', 'right'];
  let startCorner = borders.indexOf(borderSkipped, 0);

  if (startCorner === -1) {
    startCorner = 0;
  }

  function cornerAt (index) {
    return corners[(startCorner + index) % 4];
  }

  let corner = cornerAt(0);
  ctx.moveTo(corner[0], corner[1]);

  for (let i = 1; i < 4; i++) {
    corner = cornerAt(i);

    nextCornerId = i + 1;

    if (nextCornerId == 4) {
      nextCornerId = 0;
    }

    width = corners[2][0] - corners[1][0];
    height = corners[0][1] - corners[1][1];
    x = corners[1][0];
    y = corners[1][1];

    var radius = cornerRadius;

    if (radius > height / 2) {
      radius = height / 2;
    }

    if (radius > width / 2) {
      radius = width / 2;
    }

    ctx.moveTo(x + radius, y);

    if (this._chart.options.stacked) {
      if (this._datasetIndex) {
        ctx.lineTo(x + width - radius, y);
        ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
      } else {
        ctx.lineTo(x + width + radius, y);
        ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
      }

      ctx.lineTo(x + width, y + height - radius);

      if (this._datasetIndex) {
        ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
      } else {
        ctx.quadraticCurveTo(x + width, y + height, x + width + radius, y + height);
      }
    } else {
      ctx.lineTo(x + width - radius, y);
      ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
      ctx.lineTo(x + width, y + height - radius);
      ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    }

    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
  }

  ctx.fill();

  if (borderWidth) {
    ctx.stroke();
  }
};

Chart.Tooltip.positioners.custom = function (elements, eventPosition) {
  return {
    x: elements[0]._model.base * 1.45,
    y: eventPosition.y - 60,
  };
};

const { reactiveProp } = mixins;

export default {
  extends: HorizontalBar,

  mixins: [reactiveProp],

  props: {
    id: {
      type: String,
      default: '',
    },
    background: {
      type: Array,
      default: null,
    },
    options: {
      type: Object,
      default: null,
    },
    plugin: {
      type: Object,
      default: null,
    },
    datalabels: {
      type: Boolean,
      default: false,
    },
    gradient: {
      type: Boolean,
      default: false,
    },
  },

  watch: {
    options: {
      handler (options) {
        this.$data._chart.destroy();
        this.renderChart(this.chartData, options);
      },
    },
  },

  mounted () {
    const self = this;

    if (this.plugin) {
      this.addPlugin(this.plugin);
    }

    if (this.datalabels) {
      this.addPlugin(ChartjsPluginDataLabels);
    }

    this.addPlugin(ChartjsPluginStyle);

    this.options.tooltips.custom = function (tooltip) {
      let tooltipEl = document.getElementById(`${self.id}-chartjs-tooltip`);

      if (! tooltipEl) {
        tooltipEl = document.createElement('div');
        tooltipEl.id = `${self.id}-chartjs-tooltip`;
        tooltipEl.innerHTML = '<table></table>';
        this._chart.canvas.parentNode.appendChild(tooltipEl);
      }

      if (tooltip.opacity === 0) {
        tooltipEl.style.opacity = 0;
        return;
      }

      tooltipEl.classList.remove('above', 'below', 'no-transform');

      if (tooltip.yAlign) {
        tooltipEl.classList.add(tooltip.yAlign);
      } else {
        tooltipEl.classList.add('no-transform');
      }

      function getBody (bodyItem) {
        return bodyItem.lines;
      }

      if (tooltip.body) {
        const titleLines = tooltip.title || [];
        const bodyLines = tooltip.body.map(getBody);

        let innerHtml = '<thead>';

        titleLines.forEach((title) => {
          innerHtml += `<tr><th class="tooltip-title">${title}</th></tr>`;
        });

        innerHtml += '</thead><tbody>';

        bodyLines.forEach((body, i) => {
          let color = self.gradient ? self.background[i + self.gradient] : tooltip.labelColors[0].backgroundColor;

          if (i === 1) {
            color = '#D1D1D1';
          }

          const span = `<span class="chartjs-tooltip-key" style="background: ${color};"></span>`;

          innerHtml += `<tr><td class="d-flex">${span}${body}</td></tr>`;
        });

        innerHtml += '</tbody>';

        const tableRoot = tooltipEl.querySelector('table');
        tableRoot.innerHTML = innerHtml;
      }

      const positionY = this._chart.canvas.offsetTop;
      const positionX = this._chart.canvas.offsetLeft;

      tooltipEl.style.opacity = 1;
      tooltipEl.style.left = `${positionX + tooltip.caretX}px`;
      tooltipEl.style.top = `${positionY + tooltip.caretY}px`;
      tooltipEl.style.fontFamily = tooltip._bodyFontFamily;
      tooltipEl.style.fontSize = `${tooltip.bodyFontSize}px`;
      tooltipEl.style.fontStyle = tooltip._bodyFontStyle;
    };

    if (this.gradient) {
      const gradient = this.$refs.canvas
        .getContext('2d')
        .createLinearGradient(0, 0, 700, 0);

      gradient.addColorStop(0, this.background[0]);
      gradient.addColorStop(1, this.background[1]);

      this.chartData.datasets[0].backgroundColor = gradient;
    }

    this.renderChart(this.chartData, this.options);
  },
};
</script>
